import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { useState } from "react"

import { AccountPage } from "@/account/components/account-page"
import { SignedDevices } from "@/account/components/signed-devices"
import { TeamExport } from "@/account/components/team-export"
import {
  deleteAccountFormAction,
  deleteAccountFormSchema,
  getAccountPageData,
  restoreAccountAction,
  updateProfileNameAction,
} from "@/account/rpc"
import { useLogout } from "@/auth/hooks"
import { requireUserId } from "@/auth/rpc"
import config from "@/config"
import { ErrorState } from "@/core/components/app-shell"
import { useFormAction } from "@/form"
import { SellerProfileForm } from "@/invoicing/components/seller-profile-form/seller-profile-form"
import { loadSellerProfile, updateSellerProfileAction } from "@/invoicing/rpc"
import { Button } from "@/ui/button"
import { Heading } from "@/ui/typography"

export const Route = createFileRoute("/_app-shell/account")({
  // the OTP login redirects back here; the page is signed-in only
  beforeLoad: async () => {
    await requireUserId({ data: { redirectTo: "/account" } })
  },
  // the business form must not take the page down: a failed load shows an error in its section
  loader: async () => {
    const [account, seller] = await Promise.all([
      getAccountPageData(),
      loadSellerProfile().catch(() => null),
    ])
    return { ...account, seller }
  },
  head: () => ({
    meta: [
      { title: `Profile & settings - ${config.core.projectName}` },
      { name: "robots", content: "noindex" },
    ],
  }),
  pendingComponent: AccountPending,
  errorComponent: ({ reset }) => {
    const router = useRouter()
    return (
      <div className="mx-auto w-full max-w-3xl space-y-4">
        <Heading level={1}>Profile &amp; settings</Heading>
        <ErrorState
          message="Couldn't load your account."
          onRetry={() => {
            reset()
            void router.invalidate()
          }}
        />
      </div>
    )
  },
  component: AccountRoute,
})

function AccountPending() {
  return (
    <div aria-busy="true" role="status" className="mx-auto w-full max-w-3xl space-y-4">
      <Heading level={1}>Profile &amp; settings</Heading>
      <span className="sr-only">Loading your account</span>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          aria-hidden="true"
          className="bg-muted h-24 rounded-lg motion-safe:animate-pulse"
        />
      ))}
    </div>
  )
}

function AccountRoute() {
  const { email, name, seller, plan, billing, deletion, ownedTeams } = Route.useLoaderData()
  const router = useRouter()
  // re-fetch the account after every mutation so the page never shows stale state
  const refresh = () => router.invalidate()
  const queryClient = useQueryClient()
  const saveName = useServerFn(updateProfileNameAction)
  const saveSeller = useServerFn(updateSellerProfileAction)
  const [businessSaved, setBusinessSaved] = useState(false)
  const logout = useLogout()

  const deleteForm = useFormAction(deleteAccountFormSchema, deleteAccountFormAction, {
    onSuccess: refresh,
  })
  const restoreFn = useServerFn(restoreAccountAction)
  const restore = useMutation({ mutationFn: () => restoreFn(), onSuccess: refresh })

  const confirmError = deleteForm.fields.confirm.errors[0]
  const otherError = deleteForm.fields.understood.errors[0]
  const deleteError =
    confirmError || !deleteForm.error
      ? otherError
      : (deleteForm.error as { message?: string }).message

  return (
    <AccountPage
      email={email}
      name={name}
      onSaveName={async (value) => {
        await saveName({ data: { name: value } })
        // header, marketing site and this page all read the name: refetch all of them
        await Promise.all([queryClient.invalidateQueries({ queryKey: ["user"] }), refresh()])
      }}
      business={
        seller ? (
          <>
            <SellerProfileForm
              seller={seller}
              onSubmit={(request) => {
                setBusinessSaved(false)
                return saveSeller({ data: request })
              }}
              onSaved={() => {
                setBusinessSaved(true)
                void refresh()
              }}
            />
            {businessSaved && (
              <p role="status" className="text-sm">
                Business details saved.
              </p>
            )}
          </>
        ) : (
          <div className="space-y-2">
            <p role="alert" className="text-destructive text-sm">
              Couldn't load your business details.
            </p>
            <Button variant="outline" onClick={() => void refresh()}>
              Try again
            </Button>
          </div>
        )
      }
      plan={plan}
      billing={billing}
      purgeAfter={deletion?.purgeAfter ?? null}
      onDelete={deleteForm.onSubmit}
      isDeleting={deleteForm.isPending}
      confirmError={confirmError}
      deleteError={deleteError}
      onRestore={() => restore.mutate()}
      isRestoring={restore.isPending}
      restoreError={restore.error?.message}
      teamExport={ownedTeams.length > 0 ? <TeamExport teams={ownedTeams} /> : undefined}
      devices={<SignedDevices onSignedOutEverywhere={logout} />}
    />
  )
}
